import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-[#8A8A8A]">Chargement...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center gap-6">
        <h1 className="font-heading text-3xl text-[#2C2C2C]">Article non trouvé</h1>
        <Link to="/blog">
          <Button variant="outline" className="rounded-none" data-testid="back-to-blog-404">
            Retour au blog
          </Button>
        </Link>
      </div>
    );
  }

  const paragraphs = post.content.split("\n\n");

  return (
    <div data-testid="blog-post-page" className="pt-20">
      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh]">
        <img
          src={post.image_url || "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80"}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAF9F6] via-transparent to-transparent" />
      </div>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={fadeIn}>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#C5A065] hover:text-[#C5A065]/80 transition-colors mb-8" data-testid="back-to-blog">
              <ArrowLeft className="w-4 h-4" />
              Retour au blog
            </Link>

            <div className="flex items-center gap-3 mb-6">
              <Badge
                variant="secondary"
                className="rounded-none text-xs tracking-wider uppercase bg-[#E5E0D8] text-[#2C2C2C]/70"
              >
                <Tag className="w-3 h-3 mr-1" />
                {post.category}
              </Badge>
              <span className="flex items-center gap-1 text-xs text-[#8A8A8A]">
                <Calendar className="w-3 h-3" />
                {formatDate(post.published_at)}
              </span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-[#2C2C2C] leading-tight" data-testid="blog-post-title">
              {post.title}
            </h1>

            <Separator className="my-10" />

            <div className="prose-custom space-y-6">
              {paragraphs.map((p, i) => {
                if (p.startsWith("## ")) {
                  return (
                    <h2 key={i} className="font-heading text-2xl font-medium text-[#2C2C2C] mt-10 mb-4">
                      {p.replace("## ", "")}
                    </h2>
                  );
                }
                return (
                  <p key={i} className="text-base leading-relaxed text-[#2C2C2C]/75">
                    {p}
                  </p>
                );
              })}
            </div>

            <Separator className="my-12" />

            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <Link to="/blog">
                <Button
                  variant="outline"
                  className="border-[#C5A065] text-[#C5A065] hover:bg-[#C5A065] hover:text-white rounded-none px-6 py-3 text-sm tracking-widest uppercase"
                  data-testid="read-more-articles"
                >
                  Plus d'articles
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  className="bg-[#C5A065] text-white hover:bg-[#C5A065]/90 rounded-none px-6 py-3 text-sm tracking-widest uppercase"
                  data-testid="blog-post-cta"
                >
                  Nous contacter
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
